import type React from 'react';
import { type PropsWithChildren, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';
import { useTelemetryContext } from '@myphx-engineering/instrumentation/react';
import {
    Alert,
    Anchor,
    Body1,
    DividedText,
    ElevatedCard,
    Flex,
    IconButton,
    IconControlledSubstance,
    IconEdit,
    IconInfo,
    Stack,
    Subtitle1,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { getFragments } from '../../graphql';
import {
    ControlledSubstanceCode,
    type CoverageFlags,
    type MedicationInfoFragment,
} from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';
import { getAbsoluteRoute, getTotalQuantityDescriptionV2 } from '../../util';
import { ConditionalComponent } from '../utility/ConditionalComponent';

import { DrugCoverageAlerts } from './drug-coverage-alerts/DrugCoverageAlerts';
import { NotCoveredModal } from './drug-coverage-alerts/NotCoveredModal';
import { PARequiredModal } from './drug-coverage-alerts/PARequiredModal';
import { DrugFamilyEquivalents } from './DrugFamilyEquivalents';

type DrugFamilyHeaderviewProps = {
    topNode?: React.ReactNode;
    medication: MedicationInfoFragment;
    coverageFlags?: CoverageFlags;
    editClicked?: () => void;
    controlledSubstanceCode?: ControlledSubstanceCode | null;
    showEquivalents?: boolean;
};

export const DrugFamilyHeaderV2 = ({
    topNode,
    medication,
    coverageFlags,
    editClicked,
    children,
    controlledSubstanceCode,
    showEquivalents = false,
}: PropsWithChildren<DrugFamilyHeaderviewProps>) => {
    const { t } = useTranslation();
    const { disableAuthFlows, signIn, isAuthenticated } = useAuthContext();
    const { telemetryInstance } = useTelemetryContext();
    const {
        displayMedicationEquivalents,
        displayRouteOfAdministration,
        helpCenterMedicationEnabled,
    } = useFlags();
    const [isPARequiredModalOpen, { toggle: togglePARequiredModal }] =
        useDisclosure(false);
    const [isNotCoveredModalOpen, { toggle: toggleNotCoveredModal }] =
        useDisclosure(false);
    const [searchParams] = useSearchParams();

    const navigate = useNavigate();

    const chosenQuantity =
        searchParams.get('qInput') ?? searchParams.get('quantity');

    const quantity = chosenQuantity
        ? Number(chosenQuantity)
        : (medication?.defaultQuantity ?? 30);

    if (!medication) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const {
        brandNameCode,
        formCode,
        name,
        packageCode,
        packageQuantity,
        packageSize,
        packageSizeUnit,
        strengthDisplayText,
        routeOfAdministrationText,
    } = medication;

    const equivalents = medication.medicationEquivalents
        ? getFragments(medication.medicationEquivalents)
        : null;

    const formPackage = {
        formCode,
        packageCode,
        packageQuantity,
        packageSize,
        packageSizeUnit,
    };

    const roaText = displayRouteOfAdministration
        ? routeOfAdministrationText
        : null;

    const quantityDesc = getTotalQuantityDescriptionV2(
        quantity,
        formPackage,
        roaText
    );

    // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
    const login = useCallback(() => signIn(location.pathname), [signIn]);

    const handleEditClick = () => {
        //Log telemetry event
        telemetryInstance.logEvent({
            name: 'Drugconfig open edit drug',
            source: 'DrugFamilyHeaderV2',
        });
        editClicked?.();
    };

    const displayCoverageAlert =
        isAuthenticated &&
        coverageFlags &&
        (coverageFlags.requiresPriorAuthorization || !coverageFlags.isCovered);

    return (
        <>
            <Stack gap="xs" data-testid="drug-family-header">
                <ConditionalComponent
                    condition={!!controlledSubstanceCode && !isAuthenticated}
                >
                    <Alert IconOverride={IconInfo} severity="warning">
                        <Trans i18nKey="drugFamilyHeader.alert.controlledSubstances">
                            <Body1 color="warning">
                                <Anchor
                                    color="warning"
                                    onClick={
                                        disableAuthFlows ? undefined : login
                                    }
                                >
                                    Sign in to view prices
                                </Anchor>{' '}
                                for controlled substances.
                            </Body1>
                        </Trans>
                    </Alert>
                </ConditionalComponent>
                {topNode && topNode}
                <ElevatedCard>
                    <Stack gap="xxs">
                        <Flex gap="xxs">
                            <Subtitle1 data-testid="search-product-details-header">
                                {name}
                            </Subtitle1>
                            <ConditionalComponent
                                condition={!!controlledSubstanceCode}
                            >
                                <IconButton
                                    Icon={IconControlledSubstance}
                                    withStyles={false}
                                    onClick={
                                        helpCenterMedicationEnabled
                                            ? () =>
                                                  navigate(
                                                      getAbsoluteRoute(
                                                          'helpCenter.medications'
                                                      )
                                                  )
                                            : undefined
                                    }
                                />
                            </ConditionalComponent>
                        </Flex>

                        <ConditionalComponent
                            condition={
                                !!equivalents?.length &&
                                showEquivalents &&
                                displayMedicationEquivalents // temp feature flag
                            }
                        >
                            <DrugFamilyEquivalents
                                drugBrandNameCode={brandNameCode}
                                drugName={name}
                                equivalents={equivalents}
                            />
                        </ConditionalComponent>

                        {displayCoverageAlert ? (
                            <ConditionalComponent
                                condition={!!displayCoverageAlert}
                            >
                                <Flex>
                                    <DividedText
                                        entries={[
                                            strengthDisplayText ?? '',
                                            quantityDesc?.longText ?? '',
                                        ]}
                                    />
                                </Flex>
                                <Flex
                                    justify="space-between"
                                    align="center"
                                    gap="md"
                                >
                                    <DrugCoverageAlerts
                                        coverageFlags={coverageFlags}
                                        toggleNotCoveredModal={
                                            toggleNotCoveredModal
                                        }
                                        togglePARequiredModal={
                                            togglePARequiredModal
                                        }
                                    />
                                    {editClicked && (
                                        <TextButton
                                            data-testid="edit-drug-configuration"
                                            size="sm"
                                            onClick={handleEditClick}
                                            LeftIcon={IconEdit}
                                        >
                                            {t('drugConfig.Edit')}
                                        </TextButton>
                                    )}
                                </Flex>
                            </ConditionalComponent>
                        ) : (
                            <Flex
                                justify="space-between"
                                align="center"
                                gap="md"
                            >
                                <DividedText
                                    entries={[
                                        strengthDisplayText ?? '',
                                        quantityDesc?.longText ?? '',
                                    ]}
                                />
                                {editClicked && (
                                    <TextButton
                                        data-testid="edit-drug-configuration"
                                        size="sm"
                                        onClick={handleEditClick}
                                        LeftIcon={IconEdit}
                                    >
                                        {t('drugConfig.Edit')}
                                    </TextButton>
                                )}
                            </Flex>
                        )}
                    </Stack>
                </ElevatedCard>
                {children}
            </Stack>
            <PARequiredModal
                opened={isPARequiredModalOpen}
                close={togglePARequiredModal}
            />
            <NotCoveredModal
                opened={isNotCoveredModalOpen}
                close={toggleNotCoveredModal}
            />
        </>
    );
};
